import React, { useState, useEffect } from "react";
import { apiFetch, fmt } from "../api";
import { Spinner, EmptyState } from "./ui";  // UI-SWEEP-ADMIN

// ===================== AUDIT LOG PANEL =====================
// Originally Chunk 14F; relocated to System Admin compartment 2026-05-02
// (Chunk SA-B). Two stacked surfaces over the audit tables we maintain:
//   - PHI Access  → phi_access_log (every claim/diary read; HIPAA §164.312(b))
//   - Activity    → audit_log     (close events, admin overrides, etc.)
// Both gated on manage_users at the parent (Admin sub-tab visibility) AND
// re-checked server-side in /admin/audit-log + /admin/phi-access-log.
export default function AuditLogPanel({ onNavigateToClaim }) {
  const [section, setSection]               = useState("phi");
  const [phiRows, setPhiRows]               = useState([]);
  const [activityRows, setActivityRows]     = useState([]);
  const [phiLoading, setPhiLoading]         = useState(false);
  const [activityLoading, setActivityLoading] = useState(false);
  const [phiOffset, setPhiOffset]           = useState(0);
  const [activityOffset, setActivityOffset] = useState(0);
  const [phiHasMore, setPhiHasMore]         = useState(true);
  const [activityHasMore, setActivityHasMore] = useState(true);

  const loadPhi = (offset = 0, append = false) => {
    setPhiLoading(true);
    apiFetch(`/admin/phi-access-log?limit=50&offset=${offset}`)
      .then(d => {
        if (append) {
          setPhiRows(prev => [...prev, ...d]);
        } else {
          setPhiRows(d);
        }
        setPhiOffset(offset);
        setPhiHasMore(d.length === 50);
        setPhiLoading(false);
      })
      .catch(() => setPhiLoading(false));
  };
  const loadActivity = (offset = 0, append = false) => {
    setActivityLoading(true);
    apiFetch(`/admin/audit-log?limit=50&offset=${offset}`)
      .then(d => {
        if (append) {
          setActivityRows(prev => [...prev, ...d]);
        } else {
          setActivityRows(d);
        }
        setActivityOffset(offset);
        setActivityHasMore(d.length === 50);
        setActivityLoading(false);
      })
      .catch(() => setActivityLoading(false));
  };

  useEffect(() => {
    if (section === "phi" && phiRows.length === 0 && !phiLoading) loadPhi();
    if (section === "activity" && activityRows.length === 0 && !activityLoading) loadActivity();
  }, [section]);

  // Render the actor cell — name on top, "impersonated by ..." chip below
  // when the JWT carried an impersonator_id (system_admin "Login as").
  const renderActor = (row, namePrefix) => {
    const name    = row[`${namePrefix}name`]    || row[`${namePrefix}username`] || "—";
    const impName = row.impersonator_name       || row.impersonator_username;
    return (
      <div>
        <div style={{fontSize:13}}>{name}</div>
        {impName && (
          <div style={{fontSize:10, color:"#dc2626", marginTop:1, fontWeight:500}}>
            🔒 impersonated by {impName}
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="section-header">
        <span className="section-title">Audit Log</span>
        <div style={{display:"flex", gap:6}}>
          <button className={`btn btn-sm ${section==="phi" ? "btn-primary" : "btn-outline"}`}
                  onClick={() => setSection("phi")}>
            PHI Access{phiRows.length > 0 ? ` (${phiRows.length}${phiHasMore ? "+" : ""})` : ""}
          </button>
          <button className={`btn btn-sm ${section==="activity" ? "btn-primary" : "btn-outline"}`}
                  onClick={() => setSection("activity")}>
            Activity{activityRows.length > 0 ? ` (${activityRows.length}${activityHasMore ? "+" : ""})` : ""}
          </button>
        </div>
      </div>

      {section === "phi" && (
        <div className="card table-wrap">
          {phiLoading && phiRows.length === 0 ? (
            <Spinner label="Loading PHI access log…" />
          ) : phiRows.length === 0 ? (
            <EmptyState icon="🔒" title="No PHI access events" hint="No PHI access events recorded yet." />
          ) : (
            <>
              <table>
                <thead><tr>
                  <th>When</th>
                  <th>Reader</th>
                  <th>Claim</th>
                  <th>Resource</th>
                  <th>Type</th>
                  <th>IP</th>
                </tr></thead>
                <tbody>
                  {phiRows.map(r => {
                    const clickable = r.claim_id && onNavigateToClaim;
                    return (
                      <tr key={r.id}
                          onClick={() => { if (clickable) onNavigateToClaim(r.claim_id); }}
                          style={{cursor: clickable ? "pointer" : "default"}}
                          onMouseEnter={e => { if (clickable) e.currentTarget.style.background = "var(--bg2)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = ""; }}>
                        <td style={{whiteSpace:"nowrap"}}>{fmt.datetime(r.accessed_at)}</td>
                        <td>{renderActor(r, "reader_")}</td>
                        <td className="mono" style={{color: r.claim_id ? "var(--blue)" : "var(--text3)", fontWeight: 600}}>{r.claim_number || "—"}</td>
                        <td>{r.record_type || "—"}</td>
                        <td>{r.access_type || "—"}</td>
                        <td className="mono" style={{fontSize: 11, color: "var(--text3)"}}>{r.ip_address || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {phiHasMore && (
                <div style={{padding:10, textAlign:"center"}}>
                  <button className="btn btn-outline btn-sm" onClick={() => loadPhi(phiOffset + 50, true)} disabled={phiLoading}>
                    {phiLoading ? "Loading..." : "Load 50 more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {section === "activity" && (
        <div className="card table-wrap">
          {activityLoading && activityRows.length === 0 ? (
            <Spinner label="Loading activity log…" />
          ) : activityRows.length === 0 ? (
            <EmptyState icon="📋" title="No activity events" hint="No activity events recorded yet." />
          ) : (
            <>
              <table>
                <thead><tr>
                  <th>When</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Subject</th>
                  <th>IP</th>
                </tr></thead>
                <tbody>
                  {activityRows.map(r => {
                    const clickable = r.related_claim_id && onNavigateToClaim;
                    const isOverride = r.action === "admin_override_close";
                    return (
                      <tr key={r.id}
                          onClick={() => { if (clickable) onNavigateToClaim(r.related_claim_id); }}
                          style={{cursor: clickable ? "pointer" : "default"}}
                          onMouseEnter={e => { if (clickable) e.currentTarget.style.background = "var(--bg2)"; }}
                          onMouseLeave={e => { e.currentTarget.style.background = ""; }}>
                        <td style={{whiteSpace:"nowrap"}}>{fmt.datetime(r.created_at)}</td>
                        <td>{renderActor(r, "actor_")}</td>
                        <td>
                          <span className="badge" style={{
                            background: isOverride ? "#fee2e2" : "#e0e7ff",
                            color:      isOverride ? "#991b1b" : "#3730a3",
                            fontSize: 11, padding: "2px 6px",
                          }}>{r.action}</span>
                        </td>
                        <td className="mono" style={{color: r.related_claim_id ? "var(--blue)" : "var(--text3)", fontWeight: 600}}>
                          {r.related_claim_number || `${r.table_name} #${r.record_id}`}
                        </td>
                        <td className="mono" style={{fontSize: 11, color: "var(--text3)"}}>{r.ip_address || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {activityHasMore && (
                <div style={{padding:10, textAlign:"center"}}>
                  <button className="btn btn-outline btn-sm" onClick={() => loadActivity(activityOffset + 50, true)} disabled={activityLoading}>
                    {activityLoading ? "Loading..." : "Load 50 more"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
